import { FormEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CheckCircle2, ShieldX } from 'lucide-react';
import { API_ORIGIN } from '../../lib/api';
import { SiteFooter } from '../../components/SiteFooter';
import { SiteHeader } from '../../components/SiteHeader';
import { Card } from '../../components/ui/Card';
import { Reveal } from '../../components/landing/Reveal';
import { Button } from '../../components/ui/Button';
import { FormError, TextField } from '../../components/auth/FormFields';
import { CountrySelect } from '../../components/ui/CountrySelect';
import { CurrencySelect } from '../../components/ui/CurrencySelect';

interface InvitationLookup {
  workspaceName: string;
  legalName: string | null;
  email: string;
  language: string;
  expiresAt: string;
  alreadySubmitted: boolean;
}

/**
 * Public, token-gated vendor self-serve onboarding portal. The bearer
 * token in the URL IS the auth — no PlyEZ account required. Submits
 * KYC + tax-form + banking details that AP team reviews internally.
 */
export function VendorOnboardPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { t } = useTranslation();

  const lookup = useQuery<InvitationLookup>({
    queryKey: ['vendor-onboard', token],
    queryFn: async () => (await axios.get(`${API_ORIGIN}/api/vendor/onboard/${token}`)).data,
    retry: false,
  });

  const [legalName, setLegalName] = useState('');
  const [legalNameKana, setLegalNameKana] = useState('');
  const [countryCode, setCountryCode] = useState('JP');
  const [taxFormKind, setTaxFormKind] = useState<'W-9' | 'W-8BEN' | 'W-8BEN-E' | 'VAT-EU' | 'GST' | 'none'>('none');
  const [taxId, setTaxId] = useState('');
  const [jpCorpNumber, setJpCorpNumber] = useState('');
  const [jpInvoiceRegistration, setJpInvoiceRegistration] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('JPY');
  const [bankKind, setBankKind] = useState<'zengin' | 'swift' | 'ach' | 'sepa'>('zengin');
  const [bankField1, setBankField1] = useState('');
  const [bankField2, setBankField2] = useState('');
  const [bankField3, setBankField3] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () =>
      (await axios.post(`${API_ORIGIN}/api/vendor/onboard/${token}`, {
        legalName,
        legalNameKana: legalNameKana || undefined,
        countryCode,
        taxFormKind,
        taxId: taxId || undefined,
        jpCorpNumber: jpCorpNumber || undefined,
        jpInvoiceRegistration: jpInvoiceRegistration || undefined,
        defaultCurrency,
        banking: bankField1 ? { kind: bankKind, fields: bankFieldsFor(bankKind, bankField1, bankField2, bankField3) } : undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
        address: address || undefined,
      })).data,
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!legalName.trim()) { setErr(t('vendorOnboard.errors.legalName')); return; }
    submit.mutate();
  }

  if (lookup.isLoading) {
    return (
      <Frame>
        <Card className="rounded-2xl border-border p-8 text-center text-muted-foreground">{t('common.loading')}</Card>
      </Frame>
    );
  }
  if (lookup.error || !lookup.data) {
    return (
      <Frame>
        <Card className="rounded-2xl border-border p-10 text-center max-w-md mx-auto">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-warn-50 text-warn-600 dark:bg-warn-600/15 dark:text-amber-200">
            <ShieldX size={28} />
          </div>
          <h1 className="display text-xl font-semibold">{t('vendorOnboard.invalid.title')}</h1>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t('vendorOnboard.invalid.body')}</p>
        </Card>
      </Frame>
    );
  }
  if (lookup.data.alreadySubmitted || submit.isSuccess) {
    return (
      <Frame>
        <Card className="rounded-2xl border-border p-10 text-center max-w-md mx-auto">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="display text-2xl font-semibold">{t('vendorOnboard.done.title')}</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">{t('vendorOnboard.done.body')}</p>
        </Card>
      </Frame>
    );
  }

  return (
    <Frame>
      <Reveal as="div">
        <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
          <span className="sparkle-dot" aria-hidden />
          {t('vendorOnboard.eyebrow')}
        </span>
        <h1 className="display text-3xl font-semibold tracking-tight mt-4">
          {t('vendorOnboard.title', { workspaceName: lookup.data.workspaceName })}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">{t('vendorOnboard.subtitle')}</p>
      </Reveal>

      <div className="rule mt-8" aria-hidden />

      <form onSubmit={onSubmit} className="mt-8 space-y-6 max-w-2xl">
        <Card className="rounded-2xl border-border p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('vendorOnboard.identity')}</div>
          <TextField label={t('vendorOnboard.legalName')} required value={legalName} onChange={(e) => setLegalName(e.target.value)} />
          <TextField label={t('vendorOnboard.legalNameKana')} value={legalNameKana} onChange={(e) => setLegalNameKana(e.target.value)} />
          <CountrySelect label={t('vendorOnboard.country')} value={countryCode} onChange={setCountryCode} />
          <TextField label={t('vendorOnboard.jpCorpNumber')} value={jpCorpNumber} onChange={(e) => setJpCorpNumber(e.target.value)} />
          <TextField label={t('vendorOnboard.jpInvoiceReg')} value={jpInvoiceRegistration} onChange={(e) => setJpInvoiceRegistration(e.target.value)} />
        </Card>

        <Card className="rounded-2xl border-border p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('vendorOnboard.taxForm')}</div>
          <label className="block">
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1.5">{t('vendorOnboard.taxFormKind')}</div>
            <select value={taxFormKind} onChange={(e) => setTaxFormKind(e.target.value as typeof taxFormKind)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card">
              <option value="none">{t('vendorOnboard.taxFormKindNone')}</option>
              <option value="W-9">W-9 (US Person)</option>
              <option value="W-8BEN">W-8BEN (Individual)</option>
              <option value="W-8BEN-E">W-8BEN-E (Entity)</option>
              <option value="VAT-EU">EU VAT-ID</option>
              <option value="GST">GST</option>
            </select>
          </label>
          {taxFormKind !== 'none' && (
            <TextField label={t('vendorOnboard.taxId')} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          )}
        </Card>

        <Card className="rounded-2xl border-border p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('vendorOnboard.banking')}</div>
          <label className="block">
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-medium mb-1.5">{t('vendorOnboard.bankKind')}</div>
            <select value={bankKind} onChange={(e) => setBankKind(e.target.value as typeof bankKind)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card">
              <option value="zengin">{t('vendorOnboard.bankZengin')}</option>
              <option value="swift">{t('vendorOnboard.bankSwift')}</option>
              <option value="ach">{t('vendorOnboard.bankAch')}</option>
              <option value="sepa">{t('vendorOnboard.bankSepa')}</option>
            </select>
          </label>
          <TextField label={t(`vendorOnboard.bankField1.${bankKind}`)} value={bankField1} onChange={(e) => setBankField1(e.target.value)} />
          <TextField label={t(`vendorOnboard.bankField2.${bankKind}`)} value={bankField2} onChange={(e) => setBankField2(e.target.value)} />
          <TextField label={t(`vendorOnboard.bankField3.${bankKind}`)} value={bankField3} onChange={(e) => setBankField3(e.target.value)} />
          <CurrencySelect label={t('vendorOnboard.defaultCurrency')} value={defaultCurrency} onChange={setDefaultCurrency} />
        </Card>

        <Card className="rounded-2xl border-border p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t('vendorOnboard.contact')}</div>
          <TextField label={t('vendorOnboard.contactEmail')} type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          <TextField label={t('vendorOnboard.contactPhone')} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          <TextField label={t('vendorOnboard.address')} value={address} onChange={(e) => setAddress(e.target.value)} />
        </Card>

        {err && <FormError>{err}</FormError>}
        {submit.isError && <FormError>{t('vendorOnboard.submitError')}</FormError>}
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? t('common.loading') : t('vendorOnboard.submit')}
        </Button>
      </form>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-12 lg:py-16">{children}</main>
      <SiteFooter />
    </div>
  );
}

function bankFieldsFor(kind: 'zengin' | 'swift' | 'ach' | 'sepa', a: string, b: string, c: string): Record<string, string> {
  switch (kind) {
    case 'zengin': return { bankCode: a, branchCode: b, accountNumber: c };
    case 'swift':  return { bic: a, iban: b, accountHolderName: c };
    case 'ach':    return { routingNumber: a, accountNumber: b, accountHolderName: c };
    case 'sepa':   return { iban: a, bic: b, accountHolderName: c };
  }
}
